import React, { ReactElement } from "react";
import Button from "components/Common/Button";
import hljs from "highlight.js/lib/core";
import swift from "highlight.js/lib/languages/swift";
import javascript from "highlight.js/lib/languages/javascript";
import xml from "highlight.js/lib/languages/xml";
import groovy from "highlight.js/lib/languages/groovy";
import kotlin from "highlight.js/lib/languages/kotlin";
import ruby from "highlight.js/lib/languages/ruby";
import "highlight.js/styles/github.css";
import { cssColors } from "constants/css";
import * as S from "./styles";
import { SupportedLanguages, SupportedLanguagesType } from "./types";
import "./types.d";

/**
 * Register imported languages
 */
hljs.registerLanguage(SupportedLanguages.SWIFT, swift);
hljs.registerLanguage(SupportedLanguages.JAVASCRIPT, javascript);
hljs.registerLanguage(SupportedLanguages.XML, xml);
hljs.registerLanguage(SupportedLanguages.GROOVY, groovy);
hljs.registerLanguage(SupportedLanguages.KOTLIN, kotlin);
hljs.registerLanguage(SupportedLanguages.RUBY, ruby);

export interface CodeBlockProps {
  code: string;
  labelOverride?: string;
  language?: SupportedLanguagesType;
}

interface CodeBlockState {
  copied: boolean;
}

class CodeBlock extends React.Component<CodeBlockProps, CodeBlockState> {
  state: CodeBlockState = {
    copied: false
  }

  handleClipboardClickBound = this.handleClipboardClick.bind(this);
  copytimeout: number;

  /**
   * Normalizes indentation of the passed code block.
   */
  get formattedCode(): string {
    const { code } = this.props;
    const lines = code.trim().split("\n");

    const baselineWhitespace = this.findBaselineIdentation();

    return lines.map((line) => {
      const spacesInFront = line.search(/\S|$/);
      const diff = spacesInFront - baselineWhitespace;

      if (diff > 0) {
        return new Array(diff + 1).join(" ") + line.trim();
      }

      return line.trim();
    }).join("\n");
  }

  /**
   * Adds syntax highlighting based on the passed language.
   */
  get highlightCode(): string {
    const { language } = this.props;

    if (Object.values(SupportedLanguages).includes(language)) {
      const { value, illegal } = hljs.highlight(language, this.formattedCode);

      if (illegal) {
        console.warn(`Illegal code passed to ${language} code block. Syntax highlighting will not work.`);
      }

      return value;
    }

    /**
     * No styling
     */
    return this.formattedCode;
  }

  /**
   * Finds the amount of indentation used for the first line of code in the
   * string. This assumes that the first line of code should be the baseline
   * from which the rest of the code is indented.
   */
  findBaselineIdentation(): number {
    const { code } = this.props;
    const lines = code.split("\n");

    for (let i = 0; i < lines.length; i += 1) {
      // Indicates that a line contains non-space characters
      if (lines[i].replace(/\s/g, "").length) {
        return lines[i].search(/\S|$/);
      }
    }

    return 0;
  }

  /**
   * Copy to the clipboard, and handle visual copy indicator state.
   */
  async handleClipboardClick(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.formattedCode);
    } catch (e) {
      return;
    }

    this.setState({
      copied: true
    }, () => {
      clearTimeout(this.copytimeout);

      this.copytimeout = setTimeout(() => {
        this.setState({
          copied: false
        });
      }, 1500);
    });
  }

  render(): ReactElement {
    const { copied } = this.state;
    const { labelOverride, language } = this.props;

    return (
      <S.Root>
        <S.Header>
          <S.Language>
            {labelOverride || language}
          </S.Language>
          <S.ButtonContainer>
            <Button
              clear
              icon={copied ? "CheckmarkCircle" : "Copy"}
              text={copied ? "Copied" : "Copy"}
              iconColor={copied ? cssColors.colorUiGood : ""}
              onClick={this.handleClipboardClickBound}
            />
          </S.ButtonContainer>
        </S.Header>
        <S.CodeContainer>
          <S.Code
            dangerouslySetInnerHTML={{ __html: this.highlightCode }}
          />
        </S.CodeContainer>
      </S.Root>
    );
  }
}

export default CodeBlock;
